import { useMemo, useRef, useState, type FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { StoreSwitchPopup, type StoreSelection } from "../StoreSwitchPopup";
import { setStoreSelection, type RootState } from "../../../app/store";
import { MenuStoreSwitchIcon } from "../../atoms/Icon/MenuIcons";

interface StoreSelectorButtonProps {
    className?: string;
}

/**
 * 店舗選択ボタン（共通）
 *
 * ボタン + StoreSwitchPopup + Redux 書き込みを自己完結で提供する。
 * 各ページの PageHeader.rightContent に差し込んで使う。
 */
export const StoreSelectorButton: FC<StoreSelectorButtonProps> = ({ className }) => {
    const dispatch = useDispatch();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const storeSelectionState = useSelector((state: RootState) => state.storeSelection);
    const currentSelection: StoreSelection | undefined = storeSelectionState.store
        ? (storeSelectionState as StoreSelection)
        : undefined;

    const label = useMemo(() => {
        if (!currentSelection) return "店舗を選択";
        const parts = [
            currentSelection.store?.name,
            currentSelection.department?.name,
        ].filter((v): v is string => Boolean(v));
        return parts.length > 0 ? parts.join(" / ") : "店舗を選択";
    }, [currentSelection]);

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setIsOpen((v) => !v)}
                className={clsx(
                    "inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors",
                    className,
                )}
            >
                <MenuStoreSwitchIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
                <span className="truncate max-w-[220px]">{label}</span>
                <CaretDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
            </button>
            <StoreSwitchPopup
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onSelect={(selection) => {
                    dispatch(setStoreSelection(selection));
                    setIsOpen(false);
                }}
                currentSelection={currentSelection}
                anchorRef={buttonRef}
            />
        </>
    );
};

const CaretDownIcon: FC<{ className?: string }> = ({ className }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
    >
        <path d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
);
